import { memo, useEffect, useId, useRef, useState } from 'react';
import Avatar from '../profile/ui/Avatar';
import { toastError, toastSuccess } from '../../lib/toast';
import './directory.css';

/*
 * A directory card's one job: find a specific person, then reach them.
 *
 * Identity on top (the shared square Avatar, so a colleague looks the same here
 * as on their profile, the roster and Home), then a contact list pinned to the
 * bottom of the card. The list always has an email row and at least one phone
 * row ("No phone on record" when there is none), so the rows line up across a
 * grid row. Each row is the value as a mailto:/tel: link plus a copy button,
 * both 44px tall: on a phone this page is a list of tap targets.
 *
 * Nothing is reachable only by mouse hover: names and departments wrap rather
 * than truncate, and extra departments open from a "+N more" button.
 */

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-10 6L2 7" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <rect x="9" y="9" width="12" height="12" rx="1" />
    <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const COPIED_MS = 1500;

/**
 * Copy to the clipboard and say what happened. A failed write (permission
 * denied, insecure context) is reported as a failure, never as "copied".
 * Returns whether it worked, so the button can confirm in place too: at 1440px
 * the toast lands far from the click.
 */
async function copyValue(value, successMessage) {
  try {
    if (!navigator.clipboard?.writeText) throw new Error('clipboard_unavailable');
    await navigator.clipboard.writeText(value);
    // toastId per value: copying the same thing twice doesn't stack; the
    // container's limit caps different ones.
    toastSuccess(successMessage, { toastId: `copied:${value}` });
    return true;
  } catch {
    // The value is in the message (the card may hide part of it, and text in
    // a link is awkward to select), and the toast stays until dismissed
    // because it carries something the reader needs to act on.
    // closeOnClick/draggable off: a click or drag to select the text used to
    // close the toast before anything could be copied from it.
    toastError(`Couldn't copy. Here it is to copy by hand: ${value}`, {
      toastId: `copy-failed:${value}`,
      autoClose: false,
      closeOnClick: false,
      draggable: false,
    });
    return false;
  }
}

/**
 * An address shown as two parts. Every work address ends in the same domain,
 * so in a narrow card (container query in directory.css) the domain is hidden
 * and the part that identifies the person stays whole. The link's label, the
 * copied value and mailto: always carry the full address.
 */
function EmailValue({ email }) {
  const at = email.indexOf('@');
  if (at <= 0) return <span className="dir-contact__value">{email}</span>;
  return (
    <span className="dir-contact__value dir-contact__value--email">
      <span className="dir-contact__local">{email.slice(0, at)}</span>
      <span className="dir-contact__domain">{email.slice(at)}</span>
    </span>
  );
}

function ContactRow({ href, icon, value, display, linkLabel, copyLabel, copiedMessage }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const onCopy = async () => {
    const ok = await copyValue(value, copiedMessage);
    if (!ok) return;
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), COPIED_MS);
  };

  return (
    <li className="dir-contact__row">
      <a className="dir-contact__link" href={href} aria-label={linkLabel}>
        {icon}
        {display || <span className="dir-contact__value">{value}</span>}
      </a>
      <button
        type="button"
        className={`dir-contact__copy${copied ? ' is-copied' : ''}`}
        aria-label={copyLabel}
        title={copyLabel}
        onClick={onCopy}
      >
        {copied ? <CheckIcon /> : <CopyIcon />}
      </button>
    </li>
  );
}

function EmptyRow({ icon, text, className = '' }) {
  return (
    <li className={`dir-contact__row dir-contact__row--empty ${className}`.trim()}>
      <span className="dir-contact__link">
        {icon}
        <span className="dir-contact__value">{text}</span>
      </span>
      {/* Keeps the vertical hairline continuous with the rows that have one. */}
      <span className="dir-contact__copy" aria-hidden="true" />
    </li>
  );
}

function EmployeeCard({ employee }) {
  const { name, email, photo, odoo_job_title, odoo_mobile, odoo_work_phone, departments = [] } = employee;
  const headingId = useId();
  const moreId = useId();
  const [showDepts, setShowDepts] = useState(false);
  // HR often types the same number into both Odoo fields, sometimes spaced
  // differently ("+20 100 123" vs "+20100123"); compare digits, show it once.
  const digits = (p) => p.replace(/\D/g, '');
  const phones = [odoo_mobile, odoo_work_phone]
    .filter(Boolean)
    .filter((p, i, all) => all.findIndex((q) => digits(q) === digits(p)) === i);
  const primaryDept = departments[0];
  const otherDepts = departments.slice(1);

  return (
    <article className="dir-card" aria-labelledby={headingId}>
      <div className="dir-card__id">
        {/* The shared Avatar recovers from a dead photo URL with initials. */}
        <Avatar name={name} src={photo} size="dir" variant="hashed" />
        <div className="dir-identity">
          <h3 className="dir-name" id={headingId}>{name}</h3>
          {odoo_job_title && <p className="dir-title">{odoo_job_title}</p>}
          {primaryDept && (
            <div className="dir-dept">
              <p className="dir-dept__line">
                <span className="dir-dept__name">{primaryDept}</span>
                {otherDepts.length > 0 && (
                  <button
                    type="button"
                    className="dir-dept__more"
                    aria-expanded={showDepts}
                    aria-controls={moreId}
                    onClick={() => setShowDepts((v) => !v)}
                  >
                    {showDepts ? 'Show less' : `+${otherDepts.length} more`}
                  </button>
                )}
              </p>
              {otherDepts.length > 0 && (
                <ul className="dir-dept__list" id={moreId} hidden={!showDepts}>
                  {otherDepts.map((d) => <li key={d}>{d}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      <ul className="dir-contact">
        {/* An Odoo-sourced employee may have no work email or phone on record:
            muted text, never a dead mailto:/tel: link, and never left out, so
            "unknown" reads differently from a missing row. */}
        {email ? (
          <ContactRow
            href={`mailto:${email}`}
            icon={<MailIcon />}
            value={email}
            display={<EmailValue email={email} />}
            linkLabel={`Email ${name}: ${email}`}
            copyLabel={`Copy ${name}'s email address`}
            copiedMessage="Email address copied."
          />
        ) : (
          <EmptyRow icon={<MailIcon />} text="No email on record" />
        )}
        {phones.length > 0 ? phones.map((phone) => (
          <ContactRow
            key={phone}
            href={`tel:${phone.replace(/[^\d+]/g, '')}`}
            icon={<PhoneIcon />}
            value={phone}
            linkLabel={`Call ${name}: ${phone}`}
            copyLabel={`Copy ${name}'s phone number ${phone}`}
            copiedMessage="Phone number copied."
          />
        )) : (
          <EmptyRow icon={<PhoneIcon />} text="No phone on record" className="dir-contact__row--nophone" />
        )}
      </ul>
    </article>
  );
}

// Memoised: the page re-renders on every keystroke in the search box and on
// each loading flip, while the employee objects keep their identity between
// fetches, so up to 300 cards would otherwise rebuild per character typed.
export default memo(EmployeeCard);
